import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { LazyImage } from './ui/lazy-image';
import { useAuthStore } from '../store/authStore';
import { useCreditStore } from '../store/creditStore';
import CreditDisplay from './Dashboard/CreditDisplay';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const location = useLocation();
  const { isAuthenticated } = useAuthStore();

  // Use a throttled version of scroll event to improve performance
  useEffect(() => {
    let lastScrollY = window.scrollY;
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setResourcesOpen(false);
  }, [location]);

  return (
    <nav
      className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled ? 'bg-primary-dark bg-opacity-95 shadow-lg py-2 backdrop-blur-md' : 'bg-transparent py-4'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <LazyImage 
              src="https://i.ibb.co/9mpxWhP/Simple-Modern-Minimalist-Circle-Design-Studio-Logo-2.png" 
              alt="CMO Success Systems Logo" 
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/product" className="navigation-link">Platform</Link>
            <Link to="/features" className="navigation-link">Features</Link>
            <Link to="/pricing" className="navigation-link">Pricing</Link>
            <Link to="/compare" className="navigation-link">Why Us</Link>
            <div className="relative group">
              <button
                className="navigation-link flex items-center"
                onClick={() => setResourcesOpen(!resourcesOpen)}
              >
                Resources
                <ChevronDown size={16} className={`ml-1 transition-transform duration-200 ${resourcesOpen ? 'rotate-180' : ''}`} />
              </button>
              {resourcesOpen && (
                <div className="absolute top-full left-0 mt-2 bg-secondary-dark border border-gray-800 rounded-lg shadow-xl z-50 w-48 overflow-hidden">
                  <Link to="/resources" className="block px-4 py-2 text-gray-text hover:bg-primary-dark hover:text-white transition-colors">
                    All Resources
                  </Link>
                  <Link to="/blog" className="block px-4 py-2 text-gray-text hover:bg-primary-dark hover:text-white transition-colors">
                    Blog
                  </Link>
                  <Link to="/videos" className="block px-4 py-2 text-gray-text hover:bg-primary-dark hover:text-white transition-colors">
                    Videos
                  </Link>
                </div>
              )}
            </div>
            <Link to="/contact" className="navigation-link">Contact</Link>
          </div>

          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <>
                <CreditDisplay variant="compact" />
                <Link to="/dashboard" className="text-white hover:text-accent-bright transition-colors">Dashboard</Link>
              </>
            ) : (
              <>
                <Link to="/login" className="text-white hover:text-accent-bright transition-colors">Login</Link>
                <Link to="/signup" className="btn-primary">Get Started</Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-text hover:text-white focus:outline-none"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden bg-secondary-dark border-t border-gray-800 mt-2 backdrop-blur-md bg-opacity-95">
          <div className="px-4 pt-2 pb-4 space-y-4">
            <Link to="/product" className="block py-2 text-gray-text hover:text-white">Platform</Link>
            <Link to="/features" className="block py-2 text-gray-text hover:text-white">Features</Link>
            <Link to="/pricing" className="block py-2 text-gray-text hover:text-white">Pricing</Link>
            <Link to="/compare" className="block py-2 text-gray-text hover:text-white">Why Us</Link>
            
            <div>
              <button
                onClick={() => setResourcesOpen(!resourcesOpen)}
                className="flex items-center justify-between w-full py-2 text-gray-text hover:text-white"
              >
                Resources
                <ChevronDown size={16} className={`transition-transform duration-200 ${resourcesOpen ? 'rotate-180' : ''}`} />
              </button>
              {resourcesOpen && (
                <div className="pl-4 space-y-2 mt-2">
                  <Link to="/resources" className="block py-1 text-gray-text hover:text-white">
                    All Resources
                  </Link>
                  <Link to="/blog" className="block py-1 text-gray-text hover:text-white">
                    Blog
                  </Link>
                  <Link to="/videos" className="block py-1 text-gray-text hover:text-white">
                    Videos
                  </Link>
                </div>
              )}
            </div>
            
            <Link to="/contact" className="block py-2 text-gray-text hover:text-white">Contact</Link>
            
            {isAuthenticated ? (
              <div className="pt-4 flex flex-col space-y-4 border-t border-gray-800">
                <div className="flex justify-between items-center">
                  <span className="text-gray-text">Credits Balance:</span>
                  <CreditDisplay variant="compact" />
                </div>
                <Link to="/dashboard" className="py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-md text-center">
                  Dashboard
                </Link>
              </div>
            ) : (
              <div className="pt-4 flex flex-col space-y-4 border-t border-gray-800">
                <Link to="/login" className="py-2 text-center text-white hover:text-accent-bright">Login</Link>
                <Link to="/signup" className="btn-primary text-center">Get Started</Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;